'use client';

/**
 * AI platform badges — renders each major AI assistant as a polished
 * text-based wordmark card with a brand-associated color and a simple
 * abstract geometric glyph. No copyrighted brand logos are reproduced;
 * the cards use nominative reference (platform names in text) with
 * abstract marks styled in colors evocative of each platform's public
 * brand identity.
 *
 * The component drives the discovery-ready staggered-check animation
 * when the section enters the viewport — a signal that the customer's
 * AI Business Page exposes crawlable context for major AI/search surfaces.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Check } from 'lucide-react';
import { SectLabel, SectH2, SectLede } from '@/components/landing/v4-shared';
import { AI_BUSINESS_PAGE_COPY } from '@/lib/marketing/messaging-bank';

interface Platform {
  name: string;
  accent: string;
  note: string;
  Glyph: () => ReactElement;
}

function GlyphCircleDot() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
      <circle cx="16" cy="16" r="5" fill="currentColor" />
    </svg>
  );
}
function GlyphSpark() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <path d="M16 3 L19 13 L29 16 L19 19 L16 29 L13 19 L3 16 L13 13 Z" fill="currentColor" opacity="0.95" />
    </svg>
  );
}
function GlyphArc() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <path d="M6 20 a 10 10 0 0 1 20 0" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <circle cx="16" cy="22" r="2.5" fill="currentColor" />
    </svg>
  );
}
function GlyphRings() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <circle cx="16" cy="16" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.55" />
      <circle cx="16" cy="16" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16" cy="16" r="1.6" fill="currentColor" />
    </svg>
  );
}
function GlyphTiles() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <rect x="5" y="5" width="10" height="10" rx="2" fill="currentColor" opacity="0.95" />
      <rect x="17" y="5" width="10" height="10" rx="2" fill="currentColor" opacity="0.55" />
      <rect x="5" y="17" width="10" height="10" rx="2" fill="currentColor" opacity="0.55" />
      <rect x="17" y="17" width="10" height="10" rx="2" fill="currentColor" opacity="0.95" />
    </svg>
  );
}
function GlyphHex() {
  return (
    <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
      <path d="M16 3 L27 9 L27 23 L16 29 L5 23 L5 9 Z" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.45" />
      <path d="M16 10 L22 13 L22 19 L16 22 L10 19 L10 13 Z" fill="currentColor" />
    </svg>
  );
}

const PLATFORMS: readonly Platform[] = [
  { name: 'ChatGPT',    accent: '#10a37f', note: 'OpenAI · default AI search', Glyph: GlyphCircleDot },
  { name: 'Gemini',     accent: '#4285f4', note: 'Native in Google Search',   Glyph: GlyphSpark      },
  { name: 'Claude',     accent: '#d97757', note: 'Anthropic · long-form',      Glyph: GlyphArc        },
  { name: 'Perplexity', accent: '#14b8a6', note: 'Citation-heavy answers',     Glyph: GlyphRings      },
  { name: 'Google AI Overviews', accent: '#4f46e5', note: 'Above the fold',    Glyph: GlyphTiles      },
  { name: 'Bing Chat',  accent: '#0ea5e9', note: 'Microsoft Copilot-powered',  Glyph: GlyphHex        },
];

interface AiPlatformBadgesProps {
  readonly withSection?: boolean;
}

export function AiPlatformBadges({ withSection = true }: AiPlatformBadgesProps) {
  const [indexed, setIndexed] = useState<boolean[]>(
    Array(PLATFORMS.length).fill(false),
  );
  const ref = useRef<HTMLDivElement | null>(null);
  const started = useRef(false);
  const section = AI_BUSINESS_PAGE_COPY.sections.find((s) => s.id === 'ai-grid');

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setIndexed(Array(PLATFORMS.length).fill(true));
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting || started.current) continue;
          started.current = true;
          PLATFORMS.forEach((_, i) => {
            window.setTimeout(() => {
              setIndexed((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, 320 + i * 260);
          });
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const grid = (
    <div ref={ref} className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
      {PLATFORMS.map((p, i) => (
        <PlatformBadge key={p.name} platform={p} isIndexed={indexed[i]} />
      ))}
    </div>
  );

  if (!withSection) return grid;

  return (
    <section className="py-24 md:py-32 relative">
      <div className="v4-aibp-section-rule absolute top-0 inset-x-0" aria-hidden="true" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12 md:mb-16">
          <div className="mb-5 flex justify-center">
            <SectLabel color="var(--amber-glow)">{section?.eyebrow}</SectLabel>
          </div>
          <SectH2 style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: '24ch' }}>
            {section?.heading}
          </SectH2>
          <div className="mt-6 mx-auto" style={{ maxWidth: '640px' }}>
            <SectLede style={{ margin: '0 auto', textAlign: 'center' }}>
              {section?.body}
            </SectLede>
          </div>
        </div>
        {grid}
      </div>
    </section>
  );
}

function PlatformBadge({ platform, isIndexed }: { platform: Platform; isIndexed: boolean }) {
  const { name, accent, note, Glyph } = platform;
  return (
    <div
      className="v4-aibp-tile group"
      data-indexed={isIndexed}
      style={{
        borderColor: isIndexed
          ? `color-mix(in srgb, ${accent} 45%, transparent)`
          : undefined,
        boxShadow: isIndexed
          ? `0 12px 40px -18px color-mix(in srgb, ${accent} 55%, transparent), inset 0 1px 0 rgba(244,244,240,0.04)`
          : 'inset 0 1px 0 rgba(244,244,240,0.03)',
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center rounded-lg transition-colors"
          style={{
            color: accent,
            background: isIndexed
              ? `color-mix(in srgb, ${accent} 14%, transparent)`
              : 'rgba(244,244,240,0.03)',
            padding: '8px',
            filter: isIndexed
              ? `drop-shadow(0 0 8px color-mix(in srgb, ${accent} 50%, transparent))`
              : 'none',
          }}
          aria-hidden="true"
        >
          <Glyph />
        </span>
        <div className="min-w-0">
          <div
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
              color: isIndexed ? accent : 'var(--paper)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-geist-mono)',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: 'var(--dimmer)',
              marginTop: '4px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {note}
          </div>
        </div>
      </div>

      <div
        className="mt-4 pt-3 flex items-center justify-between"
        style={{ borderTop: '1px solid var(--hair)' }}
      >
        <span
          style={{
            fontFamily: 'var(--font-geist-mono)',
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            color: 'var(--dimmer)',
          }}
        >
          {isIndexed ? 'Indexed' : 'Crawling…'}
        </span>
        <span
          className="inline-flex items-center justify-center rounded-full transition-all duration-300"
          style={{
            width: 20,
            height: 20,
            border: `1px solid ${isIndexed ? 'rgba(185,246,192,0.55)' : 'var(--hair-2)'}`,
            background: isIndexed ? 'rgba(52,211,153,0.18)' : 'transparent',
            opacity: isIndexed ? 1 : 0.5,
            transform: isIndexed ? 'scale(1)' : 'scale(0.85)',
          }}
          aria-hidden="true"
        >
          {isIndexed && (
            <Check
              style={{ width: 12, height: 12, color: 'var(--jade-glow)' }}
              strokeWidth={3}
            />
          )}
        </span>
      </div>
    </div>
  );
}
